import type { BeliefBase, CustomerBelief, LeastSureItem, ProductBelief } from '../../services/api'

/**
 * Pure helpers for the least-sure list (MAR-383): the one-line evidence a row carries, and
 * the client-side fold of "the same belief about N customers on one day".
 *
 * Kept out of `LeastSure.tsx` so each is unit-tested as a function, not through a render.
 */

/** The longest evidence line a row prints before it is cut at a word. */
export const EVIDENCE_LINE_MAX = 110

/**
 * One line of where a belief came from: `From <its first evidence item>`, cut at a word
 * boundary with an ellipsis past `EVIDENCE_LINE_MAX`. The item is her own wording; it is
 * LINKED only when the server attached a receipt to a ref on that very item (MAR-390,
 * `evidenceReceiptTo`) — never by matching the words to a record here.
 *
 * `null` when she recorded no evidence: the caller says THAT in words, never a blank line.
 */
export function evidenceLineParts(
  belief: Pick<BeliefBase, 'evidence'> | null | undefined
): { text: string; index: number } | null {
  const items = Array.isArray(belief?.evidence) ? belief.evidence : []
  const index = items.findIndex((e) => typeof e === 'string' && e.trim() !== '')
  if (index < 0) return null
  const text = (items[index] as string).trim().replace(/\s+/g, ' ')
  if (text.length <= EVIDENCE_LINE_MAX) return { text, index }
  const cut = text.slice(0, EVIDENCE_LINE_MAX)
  // Back up to the last whole word; a single unbroken token is cut where it stands.
  const atWord = cut.replace(/\s+\S*$/, '')
  return { text: `${(atWord || cut).replace(/[\s,;:·.-]+$/, '')}…`, index }
}

/** The evidence line as plain text — `evidenceLineParts` with its `From `. */
export function evidenceLine(belief: Pick<BeliefBase, 'evidence'> | null | undefined): string | null {
  const parts = evidenceLineParts(belief)
  return parts ? `From ${parts.text}` : null
}

/**
 * Where evidence item `index` opens, or `null` for plain text (MAR-390). Only a ref the
 * server gave a `receipt` — an order this workspace's attribution credits to a decision —
 * links, and it opens that decision's Ledger receipt. A ref with no receipt (an order no
 * decision is credited with, a conversation, a row written before refs existed) is not a link.
 */
export function evidenceReceiptTo(
  belief: Pick<BeliefBase, 'evidenceRefs'> | null | undefined,
  index: number
): string | null {
  const refs = Array.isArray(belief?.evidenceRefs) ? belief.evidenceRefs : []
  const ref = refs.find(
    (r) => r && r.evidenceIndex === index && typeof r.receipt?.decisionId === 'string' && r.receipt.decisionId !== ''
  )
  return ref?.receipt ? `/ledger?decision=${encodeURIComponent(ref.receipt.decisionId)}` : null
}

/** The belief's id as its subject stores it — `hypothesisId` on a person, `beliefId` on a product. */
export function leastSureBeliefId(
  scope: LeastSureItem['scope'],
  belief: CustomerBelief | ProductBelief | null | undefined
): string | null {
  if (!belief) return null
  const id = scope === 'customer' ? (belief as CustomerBelief).hypothesisId : (belief as ProductBelief).beliefId
  return typeof id === 'string' && id ? id : null
}

/** The UTC calendar day of an ISO stamp (`2026-10-20`), or `null` when there is none. */
export function utcDay(iso: string | null | undefined): string | null {
  if (!iso) return null
  const t = new Date(iso)
  return Number.isNaN(t.getTime()) ? null : t.toISOString().slice(0, 10)
}

/**
 * What makes two rows "the same belief": the subject kind, the belief's `type`, its wording
 * EXACTLY (no trimming, no case-folding — a near match is a different sentence, and folding
 * it would put words in her mouth), the UTC day it was formed, and its STATUS — a confirmed
 * row and an open one are two different things to say, so they never share a line (MAR-389
 * review). `null` when any part is missing — a row with no type, no wording or no date never
 * folds. A missing status reads as `active`, as the rows render it.
 */
export function sameBeliefKey(item: LeastSureItem): string | null {
  const b = item.reason?.belief
  const day = utcDay(item.freshness ?? b?.generatedAt)
  if (!b || !b.type || !b.hypothesis || !day) return null
  return JSON.stringify([item.scope, b.type, b.hypothesis, day, b.status || 'active'])
}

export type LeastSureEntry<T extends LeastSureItem = LeastSureItem> =
  | { kind: 'one'; item: T }
  | { kind: 'group'; key: string; items: T[] }

/**
 * Fold "the same belief about N subjects on one day" into one entry (Knowledge v6 review
 * finding 02, the client-side half — the canvas's be7).
 *
 * A group takes the place of its FIRST member, so the server's order (least sure first) is
 * kept for everything that does not fold, and a group sits where its least-sure member
 * would have. Members keep their own order inside it. A key held by one row is no group.
 *
 * ⚠️ A group is about DISTINCT subjects. A subject's second row under one key (the same
 * sentence twice for one person — a regeneration, say) never joins the group: it would read
 * "about 2 customers · Dee, Dee". It stays its own line.
 *
 * ⚠️ Folding changes how rows read, never what is settled: each member keeps its own verdict
 * (there is no batch verdict route), so the group opens to one row per subject.
 */
export function foldSameBelief<T extends LeastSureItem>(items: T[]): LeastSureEntry<T>[] {
  const members = new Map<string, T[]>()
  for (const item of items) {
    const key = sameBeliefKey(item)
    if (!key) continue
    const list = members.get(key) ?? []
    if (list.some((m) => m.id === item.id)) continue
    members.set(key, [...list, item])
  }
  const grouped = new Set<T>()
  for (const list of members.values()) if (list.length >= 2) list.forEach((m) => grouped.add(m))
  const out: LeastSureEntry<T>[] = []
  const placed = new Set<string>()
  for (const item of items) {
    const key = sameBeliefKey(item)
    const group = key ? members.get(key) : undefined
    if (!key || !group || !grouped.has(item)) {
      out.push({ kind: 'one', item })
      continue
    }
    if (placed.has(key)) continue
    placed.add(key)
    out.push({ kind: 'group', key, items: group })
  }
  return out
}

/** `35–40% sure` across a group's own numbers, or `35% sure` when they agree. */
export function confidenceRange(items: LeastSureItem[]): string {
  const pcts = items.map((i) => Math.round(Math.max(0, Math.min(1, i.confidence)) * 100))
  const lo = Math.min(...pcts)
  const hi = Math.max(...pcts)
  return lo === hi ? `${lo}% sure` : `${lo}–${hi}% sure`
}

/**
 * One belief on one subject, as a key — the scope, the subject and the belief's own id. The
 * pattern cards and the one-by-one list compare on it (MAR-401), so a belief listed in a
 * pattern is never listed again below it. `null` for a belief with no id: it cannot be matched,
 * so it is never removed.
 */
export function beliefItemKey(item: LeastSureItem): string | null {
  const id = leastSureBeliefId(item.scope, item.reason.belief)
  return id ? JSON.stringify([item.scope, item.id, id]) : null
}

/**
 * The evidence line a GROUP may print (MAR-401): only when every member's first evidence item
 * reads the same — then it is true of all of them. Any difference, or any member with none,
 * and the group prints none: one member's "From …" over the group would claim it for the rest.
 */
export function sharedEvidenceLine(items: LeastSureItem[]): string | null {
  if (items.length === 0) return null
  let shared: string | null = null
  for (const it of items) {
    const parts = evidenceLineParts(it.reason.belief)
    if (!parts) return null
    if (shared === null) shared = parts.text
    else if (parts.text !== shared) return null
  }
  return shared
}

/**
 * A "formed" date (MAR-401): `22 Oct` in the current year, `22 Oct 2025` otherwise — the
 * year is only noise when it is this one. UTC, like every date on this surface. `now` is a
 * parameter so a test pins the year rather than rotting on 1 January.
 */
export function formedDate(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const sameYear = d.getUTCFullYear() === now.getUTCFullYear()
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  })
}

/** The merchant's words for a belief type. An unknown type is shown as stored, never hidden. */
const TYPE_LABEL: Record<string, string> = {
  gift: 'Gifts',
  occasion: 'Occasions',
  project_hobby: 'Projects and hobbies',
  fit_preference: 'Fit',
  replenishment: 'Buying again',
  channel_preference: 'How they like to be reached',
  fit_sizing: 'Fit and sizing',
  ideal_for: 'Who it suits',
  not_ideal_for: 'Who it does not suit',
  quality_concern: 'Quality',
  pairs_with: 'Goes well with',
  demand_signal: 'Demand',
}

export function typeLabel(type: string | null | undefined): string {
  if (!type) return 'Belief'
  return TYPE_LABEL[type] ?? type.replace(/_/g, ' ')
}
